import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Globe, Search, Loader2, Check } from "lucide-react";

const LanguageSwitcher = () => {
  const { lang, setLang, languages, loading, error } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter(
      (l) =>
        l.label.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
    );
  }, [languages, query]);

  const handleSelect = async (code) => {
    await setLang(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={ref} className="relative flex-shrink-0" data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        data-testid="language-switcher-button"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-200 hover:border-[#E8792B] hover:bg-orange-50 transition-all text-xs font-medium text-gray-700 disabled:opacity-60 disabled:cursor-wait whitespace-nowrap"
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 text-[#E8792B] animate-spin flex-shrink-0" />
        ) : (
          <Globe className="w-3.5 h-3.5 text-[#E8792B] flex-shrink-0" />
        )}
        <span className="flex-shrink-0 text-sm">{current?.flag}</span>
        <span className="hidden sm:inline max-w-[90px] truncate">{current?.nativeName}</span>
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[100] w-[280px] ltr:right-0 rtl:left-0"
          data-testid="language-switcher-dropdown"
        >
          {/* Search */}
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200 focus-within:border-[#E8792B]">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search language..."
                data-testid="language-search-input"
                className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400"
                dir="ltr"
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-[320px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No languages match &ldquo;{query}&rdquo;
              </div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.code}
                  onClick={() => handleSelect(l.code)}
                  data-testid={`language-option-${l.code}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-orange-50 ${
                    lang === l.code
                      ? "bg-orange-50 text-[#E8792B] font-bold"
                      : "text-gray-700"
                  }`}
                  dir={l.dir}
                >
                  <span className="text-lg flex-shrink-0">{l.flag}</span>
                  <div className="flex-1 ltr:text-left rtl:text-right">
                    <div className="font-medium">{l.nativeName}</div>
                    <div className="text-xs text-gray-400">{l.label}</div>
                  </div>
                  {lang === l.code && (
                    <Check className="w-4 h-4 text-[#E8792B] flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>

          {error && (
            <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-red-100">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
